import {Box, useDevice, useElementSize} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  XAxis,
  ReferenceLine,
  Tooltip,
} from 'recharts';
import {useTheme} from 'styled-components';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {isLastIndexInArray, suffixTestId, TestIdProps} from 'shared/web';

import {PRINT_CHART_WIDTH} from '../../../../../constants/printChartWidth';
import {isInCypress} from '../../../../../utils/isInCypress';
import {useIsPrintForced} from '../../../hooks/useIsPrintForced';
import {CostItem} from '../types/CostItem';
import {getDataForWaterfallChart} from '../utils/getDataForWaterfallChart';
import {ChartTooltip} from './ChartTooltip';

type TooltipState = {
  index: number;
  coordinate: number;
} | null;

interface WaterfallChartProps extends TestIdProps {
  data?: CostItem[];
  currency: string;
}

export function WaterfallChart(props: WaterfallChartProps) {
  const theme = useTheme();
  const device = useDevice();
  const {getResponsiveValue} = useIsPrintForced();
  const formatCurrency = useFormatCurrency();
  const [containerRef, containerWidth] = useElementSize<HTMLDivElement>();
  const [tooltip, setTooltip] = useState<TooltipState>(null);

  const waterfallData = getDataForWaterfallChart(props.data ?? []);

  const isFirstValue = (value: number) =>
    waterfallData.length > 0 && value === waterfallData[0].value;
  const formatLabel = (value: number) =>
    formatCurrency(isFirstValue(value) ? value : -1 * value, props.currency);

  const isSmallDevice = device === 'mobile' || device === 'tablet';
  const chartWidth = getResponsiveValue('100%', '100%', '100%', '100%', PRINT_CHART_WIDTH);
  const isAnimationActive = getResponsiveValue(true, true, true, true, false) && !isInCypress;
  const maxLabelLength = getResponsiveValue(15, 15, 12, 20, 25);

  return (
    <Box ref={containerRef} position="relative" data-testid={suffixTestId('waterfallChart', props)}>
      <ResponsiveContainer width={chartWidth} height={250}>
        <BarChart
          data={waterfallData}
          layout="horizontal"
          margin={{top: 16, right: isSmallDevice ? 16 : 0, bottom: isSmallDevice ? 64 : 0}}
        >
          <ReferenceLine y={0} stroke={theme.colors.general.separator} strokeWidth={1} />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{fontSize: 12, fill: theme.colors.text.secondary}}
            angle={isSmallDevice ? 60 : 0}
            textAnchor={isSmallDevice ? 'start' : 'middle'}
            interval={0}
            tickFormatter={(value: string) =>
              value.length > maxLabelLength ? `${value.slice(0, maxLabelLength)}…` : value
            }
            onMouseEnter={(params) =>
              setTooltip({index: params.index, coordinate: params.coordinate})
            }
            onMouseLeave={() => setTooltip(null)}
          />

          <Tooltip // Tooltip displayed on bar hover
            content={(props) => (
              <ChartTooltip
                isVisible={props.active}
                content={props.payload?.[0]?.payload?.tooltip}
              />
            )}
            wrapperStyle={{outline: 'none'}}
            cursor={false}
          />

          {/* Transparent bars for positioning (the "floating" part) */}
          <Bar dataKey="basePosition" stackId="a" fill="transparent" isAnimationActive={false} />

          {/* The actual colored bars with values */}
          <Bar
            dataKey="visibleValue"
            stackId="a"
            barSize={40}
            isAnimationActive={isAnimationActive}
          >
            {waterfallData?.map((entry, index) => {
              // First item (total costs)
              if (index === 0) {
                return (
                  <Cell key={`cell-${entry.name}`} fill={theme.colors.palettes.orange[60][100]} />
                );
              }
              // Last item (dealer margin)
              else if (isLastIndexInArray(waterfallData, index)) {
                return (
                  <Cell key={`cell-${entry.name}`} fill={theme.colors.palettes.neutral[70][100]} />
                );
              }
              // Middle items (costs)
              else {
                return (
                  <Cell key={`cell-${entry.name}`} fill={theme.colors.palettes.orange[30][100]} />
                );
              }
            })}

            {/* Label for the first or positive bars */}
            <LabelList
              dataKey="value"
              position="top"
              formatter={(value: number) =>
                isFirstValue(value) || value > 0 ? formatLabel(value) : ''
              }
              fill={theme.colors.text.secondary}
              fontSize={12}
            />
            {/* Label for the other negative bars */}
            <LabelList
              dataKey="value"
              position="bottom"
              formatter={(value: number) =>
                !isFirstValue(value) && value < 0 ? formatLabel(value) : ''
              }
              fill={theme.colors.text.secondary}
              fontSize={12}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <ChartTooltip // Tooltip displayed on label hover
        isVisible={isNotNil(tooltip)}
        left={tooltip?.coordinate ?? 0}
        bottom={32}
        content={isNotNil(tooltip) ? waterfallData[tooltip.index]?.tooltip : null}
        containerWidth={containerWidth}
      />
    </Box>
  );
}
